import { type ReactNode, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useAuthStore } from '@/shared/store/authStore'
import { useVisitRouteStore } from '@/features/visitplan/routeStore'
import { closeBottomSheet, closeModal } from '@/shared/ui'
import { listenFromHost, postToHost } from '@/shared/bridge/bridge'
import { hasHostShell } from '@/shared/bridge/env'

interface BridgeProviderProps {
  children: ReactNode
  /** 앱 뒤로가기가 여기서 멈추고 호스트로 넘어가는 역할별 홈 경로 */
  homePath: string
  /** 호스트 헤더 제목 — 역할별 메뉴에서 찾는다 */
  titleForPath: (pathname: string) => string | undefined
}

function isSafeInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

function closeOverlaysForHostNavigation() {
  closeModal({ skipHistoryBack: true })
  closeBottomSheet({ skipHistoryBack: true })
}

function closeVisitRouteFullscreen() {
  const { fullscreenOpen, setFullscreenOpen } = useVisitRouteStore.getState()
  if (!fullscreenOpen) return false

  setFullscreenOpen(false)
  return true
}

export function BridgeProvider({ children, homePath, titleForPath }: BridgeProviderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const setToken = useAuthStore((state) => state.setToken)
  const lastInboundPath = useRef<string | null>(null)
  const locationRef = useRef(location)

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    if (!hasHostShell()) return undefined
    postToHost({ type: 'READY' })

    return listenFromHost((msg) => {
      if (msg.type === 'SET_TOKEN') {
        setToken(msg.payload.accessToken, null)
        return
      }

      if (msg.type === 'GO_BACK') {
        closeOverlaysForHostNavigation()
        if (closeVisitRouteFullscreen()) return

        const currentPath = `${locationRef.current.pathname}${locationRef.current.search}`
        if (currentPath !== homePath && window.history.length > 1) {
          navigate(-1)
          return
        }
        postToHost({ type: 'GO_BACK' })
        return
      }

      if (msg.type === 'NAVIGATE' && isSafeInternalPath(msg.payload.path)) {
        closeOverlaysForHostNavigation()
        closeVisitRouteFullscreen()
        lastInboundPath.current = msg.payload.path
        navigate(msg.payload.path)
      }
    })
  }, [homePath, navigate, setToken])

  useEffect(() => {
    if (!hasHostShell()) return
    const path = `${location.pathname}${location.search}`
    if (lastInboundPath.current === path) {
      lastInboundPath.current = null
      return
    }
    postToHost({ type: 'NAVIGATE', payload: { path, title: titleForPath(location.pathname) } })
  }, [location.pathname, location.search, titleForPath])

  return <>{children}</>
}
